// The Coordinator Workbench's slots (lane contract C-B10-13): a later lane mounts on the Workbench without editing it.
// B10 S21 registers its *Saved views* in the filter bar and its acknowledgment widget beside the queue; its drill-in from
// Coordinator operations lands on `#workbench?type=&status=&risk=`, which parseQuery already reads. A slot receives the
// page's parsed query, the rows the server returned for it and `apply`, which navigates to the Workbench under another
// query, so a saved view is a query and never a second read. This module imports nothing from a lane.

import type { ComponentType } from "react";
import type { WorkbenchRowOut } from "@/api/tools";
import type { WorkbenchQuery } from "./workbench";

/** Where a slot renders: in the filter bar after the Workbench's own chips, or beside the queue under the filters. */
export type WorkbenchPlace = "filters" | "aside";

export const WORKBENCH_PLACES: readonly WorkbenchPlace[] = ["filters", "aside"];

export interface WorkbenchSlotProps {
  query: WorkbenchQuery;
  rows: readonly WorkbenchRowOut[];
  apply: (next: WorkbenchQuery) => void;
}

export interface WorkbenchSlot {
  readonly key: string;
  readonly place: WorkbenchPlace;
  readonly component: ComponentType<WorkbenchSlotProps>;
  readonly lane: string;
  /** The manifest tools the slot reads (any one is enough); absent from the manifest, the slot does not render. */
  readonly gates: readonly string[];
  readonly order: number;
}

export interface WorkbenchSlotOptions {
  lane: string;
  gates?: readonly string[] | undefined;
}

export interface WorkbenchSlots {
  register(key: string, place: WorkbenchPlace, component: ComponentType<WorkbenchSlotProps>, opts: WorkbenchSlotOptions): void;
  at(place: WorkbenchPlace, can?: (tool: string) => boolean): readonly WorkbenchSlot[];
}

export function createWorkbenchSlots(): WorkbenchSlots {
  const list: WorkbenchSlot[] = [];
  return {
    register(key, place, component, opts) {
      if (!WORKBENCH_PLACES.includes(place)) throw new Error(`workbench slot ${key}: unknown place ${String(place)}`);
      const first = list.find((slot) => slot.key === key);
      if (first) throw new Error(`workbench slot ${key} is registered twice (${first.lane} and ${opts.lane})`);
      list.push({ key, place, component, lane: opts.lane, gates: opts.gates ?? [], order: list.length });
    },
    at(place, can) {
      return list.filter((slot) => slot.place === place && (slot.gates.length === 0 || !can || slot.gates.some(can)));
    },
  };
}

/** The Workbench's one slot registry: lanes register at import (from their own register.ts); the Workbench reads it. */
export const workbenchSlots: WorkbenchSlots = createWorkbenchSlots();

export function registerWorkbenchSlot(
  key: string, place: WorkbenchPlace, component: ComponentType<WorkbenchSlotProps>, opts: WorkbenchSlotOptions,
): void {
  workbenchSlots.register(key, place, component, opts);
}
